// Lightweight structural validator for Espacenet-style ftxt queries.
// Returns human-readable warnings; non-blocking (the user may submit anyway).

const BOOL_OPS = ['AND', 'OR', 'NOT'];

export function validateFtxtQuery(query: string | null | undefined): string[] {
  const warnings: string[] = [];
  const q = (query ?? '').trim();
  if (!q) return warnings;

  // Unbalanced parentheses
  let depth = 0;
  for (const ch of q) {
    if (ch === '(') depth++;
    else if (ch === ')') depth--;
    if (depth < 0) break;
  }
  if (depth !== 0) {
    warnings.push('Unbalanced parentheses.');
  }

  // Unbalanced double quotes
  if ((q.match(/"/g)?.length ?? 0) % 2 !== 0) {
    warnings.push('Unbalanced double quotes (").');
  }

  // Stray commas (Espacenet boolean syntax uses operators, not commas)
  if (q.includes(',')) {
    warnings.push('Unexpected comma — use AND / OR / NOT between terms.');
  }

  // Tokenize on whitespace to inspect operator placement
  const tokens = q.split(/\s+/).filter(Boolean);
  const upper = tokens.map(t => t.toUpperCase());

  // Leading / trailing boolean operator
  if (BOOL_OPS.includes(upper[0])) {
    warnings.push(`Query starts with "${tokens[0]}".`);
  }
  if (BOOL_OPS.includes(upper[upper.length - 1])) {
    warnings.push(`Query ends with a dangling "${tokens[tokens.length - 1]}".`);
  }

  // Doubled operators (AND AND, OR OR, etc.)
  for (let i = 1; i < upper.length; i++) {
    if (BOOL_OPS.includes(upper[i]) && BOOL_OPS.includes(upper[i - 1])) {
      // "AND NOT" handled separately below
      if (!(upper[i - 1] === 'AND' && upper[i] === 'NOT')) {
        warnings.push(`Consecutive operators "${tokens[i - 1]} ${tokens[i]}".`);
      }
    }
  }

  // "AND NOT" — Espacenet uses NOT on its own
  if (/\bAND\s+NOT\b/i.test(q)) {
    warnings.push('Use "NOT" instead of "AND NOT".');
  }

  // Empty ftxt clause: "ftxt =" not followed by a quoted term.
  // Whitespace lives inside the lookahead so greedy \s* can't backtrack past the quote.
  if (/ftxt\s*=(?!\s*")/i.test(q)) {
    warnings.push('An "ftxt =" clause is missing its search term.');
  }

  return warnings;
}
